import { Link, useParams } from "react-router-dom";
import { BRAND, PHONE_DISPLAY, WA_NUMBER } from "@/lib/api";
import { Eyebrow } from "@/components/Reveal";

const OWNER = `${BRAND} — WhatsApp y teléfono: ${PHONE_DISPLAY}`;
const CIF = "[CIF — pendiente de aportar por el titular]";

const DOCS = {
  "aviso-legal": {
    title: "Aviso Legal",
    intro: `En cumplimiento del deber de información recogido en la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE), se informa:`,
    sections: [
      ["Titular", `${BRAND}\n${CIF}\nContacto: WhatsApp y teléfono ${PHONE_DISPLAY} (${WA_NUMBER}).`],
      ["Objeto", "Este sitio web tiene por objeto la presentación y promoción de servicios inmobiliarios (compra, venta y alquiler de propiedades), así como la recepción de consultas de clientes a través de WhatsApp y del formulario de contacto."],
      ["Condiciones de uso", "El acceso y navegación por este sitio implica la aceptación de las presentes condiciones. El usuario se compromete a hacer un uso adecuado de los contenidos y a no emplearlos para actividades ilícitas o lesivas de derechos de terceros."],
      ["Propiedad intelectual e industrial", `Todos los contenidos de este sitio (textos, fotografías, logotipo, diseño y elementos gráficos) son titularidad de ${BRAND} o de sus legítimos propietarios, y están protegidos por la normativa de propiedad intelectual e industrial. Queda prohibida su reproducción, distribución o transformación sin autorización expresa.`],
      ["Fotografías", "Las imágenes de las propiedades mostradas en este sitio son facilitadas por el titular de cada inmueble o por la propia agencia, y se muestran únicamente con fines ilustrativos de los inmuebles publicados."],
      ["Responsabilidad", `${BRAND} no se hace responsable del mal uso que los usuarios hagan de los contenidos de esta web, ni de los daños derivados de interrupciones del servicio, virus u otros elementos ajenos a su control. La información de las propiedades publicadas se ofrece de buena fe, pudiendo contener errores u omitirse sin previo aviso.`],
      ["Enlaces externos", "Este sitio puede contener enlaces a servicios de terceros (por ejemplo, WhatsApp). No nos hacemos responsable de las políticas de privacidad o contenidos de dichos servicios."],
      ["Legislación aplicable", "Las presentes condiciones se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales que correspondan conforme a derecho."],
    ],
  },
  privacidad: {
    title: "Política de Privacidad (RGPD)",
    intro: `En cumplimiento del Reglamento (UE) 2016/679 (RGPD) y de la Ley Orgánica 3/2018 de Protección de Datos Personales, te informamos sobre el tratamiento de tus datos personales.`,
    sections: [
      ["Responsable del tratamiento", `${BRAND}\n${CIF}\nContacto: WhatsApp y teléfono ${PHONE_DISPLAY}.`],
      ["Datos que recabamos", "Nombre, teléfono, dirección de correo electrónico (opcional) y el contenido del mensaje que nos envíes a través del formulario de contacto o de WhatsApp."],
      ["Finalidad del tratamiento", "Gestionar tus consultas, coordinar visitas a propiedades, atenderte por WhatsApp y, en su caso, tramitar operaciones de compraventa o alquiler."],
      ["Legitimación", "Tu consentimiento expreso al enviar el formulario o escribirnos por WhatsApp, así como la aplicación de medidas precontractuales o contractuales cuando proceda."],
      ["Conservación", "Conservaremos tus datos durante el tiempo necesario para atender tu consulta y, en su caso, durante la vigencia de la relación comercial y los plazos legales aplicables."],
      ["Destinatarios", "No cedemos tus datos a terceros salvo obligación legal. Las comunicaciones por WhatsApp se realizan a través de la plataforma WhatsApp (WhatsApp Ireland Ltd.), conforme a su propia política de privacidad."],
      ["Tus derechos", "Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición escribiéndonos por WhatsApp o teléfono al ${PHONE_DISPLAY}. También puedes retirar tu consentimiento en cualquier momento.".replace("${PHONE_DISPLAY}", PHONE_DISPLAY)],
      ["Reclamación", "Si consideras que no hemos atendido correctamente tus derechos, puedes presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es)."],
      ["Seguridad", "Aplicamos medidas técnicas y organizativas apropiadas para proteger tus datos personales contra el acceso no autorizado o la divulgación indebida."],
    ],
  },
  cookies: {
    title: "Política de Cookies",
    intro: "Una cookie es un pequeño archivo que los sitios web almacenan en tu dispositivo para recordar información sobre tu visita.",
    sections: [
      ["Cookies que utilizamos", "Este sitio web no utiliza cookies de seguimiento, analítica ni publicidad. Únicamente se emplean cookies estrictamente técnicas y de sesión, necesarias para el funcionamiento del panel de administración (mantener tu sesión iniciada de forma segura)."],
      ["Cookies de terceros", "Al interactuar con los enlaces de WhatsApp, la plataforma de WhatsApp puede establecer sus propias cookies conforme a su política de privacidad, ajena a este sitio web."],
      ["Cómo gestionar las cookies", "Puedes eliminar o bloquear las cookies desde la configuración de tu navegador en cualquier momento. La desactivación de cookies técnicas puede impedir el correcto funcionamiento del panel de administración."],
      ["Más información", `Si tienes cualquier duda sobre esta política, escríbenos por WhatsApp al ${PHONE_DISPLAY}.`],
    ],
  },
};

const Legal = () => {
  const { slug } = useParams();
  const doc = DOCS[slug];

  if (!doc)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink px-6 text-center">
        <p className="font-serif text-3xl text-ivory">Documento no encontrado</p>
        <Link to="/" data-testid="legal-notfound-back" className="rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">
          Volver al inicio
        </Link>
      </div>
    );

  return (
    <main className="mx-auto max-w-4xl px-6 pb-28 pt-32 md:pt-40">
      <Eyebrow>Información legal</Eyebrow>
      <h1 className="mt-4 font-serif text-4xl uppercase tracking-wide text-ivory md:text-5xl" data-testid="legal-title">
        {doc.title}
      </h1>
      <p className="mt-6 leading-relaxed text-stone2" data-testid="legal-body">
        {doc.intro}
      </p>
      <div className="mt-12 space-y-10">
        {doc.sections.map(([h, body]) => (
          <section key={h}>
            <h2 className="font-serif text-2xl text-gold">{h}</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-stone2">{body}</p>
          </section>
        ))}
      </div>
      <p className="mt-16 text-xs text-stone2/70">
        Documento informativo facilitado por {OWNER}. Última actualización: {new Date().toLocaleDateString("es-ES", { month: "long", year: "numeric" })}.
      </p>
    </main>
  );
};

export default Legal;
